import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import PageMeta from '@/components/base/PageMeta';
import ArticleCard from '@/components/base/ArticleCard';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

const featuredArticles = articles.filter((a) => a.featured).slice(0, 4);
const latestArticles = articles.slice(0, 6);
const hotArticles = articles.filter((a) => a.hot).slice(0, 4);

const faqData = [
  { q: '什么是白菜价商品？', a: '白菜价商品指的是价格极低、性价比超高的商品。这些商品通常通过平台补贴、限时秒杀、新人专享等方式实现超低价格，让消费者以远低于市场价的价格购买到心仪的商品。' },
  { q: '如何第一时间抢到白菜好物？', a: '建议关注我们的网站并订阅每日推送，我们会实时更新最新的白菜优惠信息。同时建议提前注册各平台账号、设置好收货地址和支付方式，在秒杀开始前做好准备，提高抢购成功率。' },
  { q: '0元购活动是真实的吗？', a: '是的，0元购活动是各大电商平台推出的真实优惠活动。通常面向新用户或特定活动期间，用户可以通过领取新人礼包、邀请好友等方式免费获得商品。所有活动信息均来自平台官方，真实可靠。' },
  { q: '优惠券有使用限制吗？', a: '不同优惠券的使用规则不同。部分优惠券全场通用无门槛，部分需要满足最低消费金额，还有一些仅限特定品类或商家使用。建议在领取前仔细阅读使用说明，合理规划消费。' },
  { q: '如何辨别虚假优惠信息？', a: '我们推荐的所有优惠活动均经过人工核实，信息来源为各平台官方渠道。建议消费者认准官方标识，避免点击来路不明的链接。如在其他渠道看到过于夸张的优惠信息，请保持警惕。' },
];

export default function Home() {
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const [subscribed, setSubscribed] = useState(false);
  const [formError, setFormError] = useState('');

  const handleNewsletterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setSubscribed(true);
      return;
    }
    try {
      const res = await fetch('https://readdy.ai/api/form/d9snn4llbrt1qcgaj6kg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ email: formData.get('email') as string }).toString(),
      });
      const text = await res.text();
      let parsed: any = {};
      try { parsed = JSON.parse(text); } catch (_) { /* ignore parse error */ }
      if (res.ok && parsed?.code === 'OK') {
        setSubscribed(true);
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || text;
        setFormError(serverMsg.includes('spam') ? '提交失败，请稍后重试。' : (serverMsg || '提交失败，请稍后重试。'));
      }
    } catch {
      setFormError('网络错误，请检查网络后重试。');
    }
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: '白菜网站',
        url: 'https://www.baicaiwang.com/',
        description: '汇聚全网最新白菜价商品、0元购活动、限时免费领取优惠，帮你第一时间抢到最新白菜好物。',
      },
      {
        '@type': 'WebSite',
        name: '白菜网站',
        url: 'https://www.baicaiwang.com/',
        inLanguage: 'zh-CN',
      },
      {
        '@type': 'WebPage',
        name: '白菜网站最新2024｜最新白菜优惠活动汇总',
        description: '白菜网站最新资讯，每日更新最新白菜价商品、0元购活动、限时免费领取优惠，汇聚全网最新白菜网站推荐。',
        url: 'https://www.baicaiwang.com/',
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [{ '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.baicaiwang.com/' }],
        },
      },
      {
        '@type': 'FAQPage',
        mainEntity: faqData.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      },
    ],
  };

  return (
    <>
      <PageMeta
        title="白菜网站最新2024｜最新白菜优惠活动汇总"
        description="白菜网站最新资讯，每日更新最新白菜价商品、0元购活动、限时免费领取优惠，汇聚全网最新白菜网站推荐，帮你第一时间抢到最新白菜好物，省钱攻略一网打尽。"
        keywords="白菜网站最新,最新白菜网站,白菜价商品,白菜优惠活动,0元购,白菜好物推荐,限时免费领取"
        canonicalUrl="https://www.baicaiwang.com/"
      />
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[420px] md:h-[560px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20abstract%20composition%20with%20warm%20orange%20and%20cream%20color%20palette%2C%20soft%20geometric%20shapes%20representing%20shopping%20deals%20and%20discounts%2C%20floating%20price%20tag%20elements%2C%20clean%20editorial%20design%20with%20negative%20space%2C%20artistic%20and%20stylized%20digital%20art%2C%20warm%20ambient%20lighting%2C%20no%20realistic%20photos&width=1600&height=900&seq=hero-bg-2024&orientation=landscape"
            alt="白菜网站 - 最新优惠活动"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
          <div className="relative z-10 w-full h-full flex flex-col items-center justify-center px-4 md:px-6 text-center">
            <span className="inline-block px-4 py-1.5 rounded-full bg-primary-500/90 text-background-50 text-sm font-medium mb-5 whitespace-nowrap backdrop-blur-sm">
              每日更新 · 全网最新白菜优惠
            </span>
            <h1 className="font-heading text-3xl md:text-5xl text-background-50 leading-tight max-w-3xl">
              汇聚全网<strong className="text-primary-300">白菜好物</strong>，省钱攻略一网打尽
            </h1>
            <p className="mt-4 md:mt-5 text-sm md:text-base text-background-200 max-w-xl leading-relaxed">
              每日更新最新白菜价商品、0元购活动、限时免费领取优惠，帮你第一时间抢到最新白菜好物
            </p>
            <div className="flex items-center gap-3 mt-8 flex-wrap justify-center">
              <Link
                to="/category/baicai-jia"
                className="px-6 py-3 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                查看白菜价商品
              </Link>
              <Link
                to="/about"
                className="px-6 py-3 rounded-lg bg-background-50/20 text-background-50 font-medium text-sm hover:bg-background-50/30 backdrop-blur-sm transition-colors whitespace-nowrap cursor-pointer"
              >
                了解更多
              </Link>
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950 text-center mb-8">
              浏览分类
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="flex items-center gap-3 p-4 rounded-xl bg-background-100 hover:bg-primary-50 border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer group"
                >
                  <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-50 text-primary-500 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                    <i className={`${cat.icon} text-lg`}></i>
                  </span>
                  <div>
                    <span className="block text-sm font-medium text-foreground-900 whitespace-nowrap">{cat.name}</span>
                    <span className="block text-xs text-foreground-500 mt-0.5">{cat.description}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Articles */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950">
                精选推荐
              </h2>
              <Link
                to="/category/baicai-jia"
                className="text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap cursor-pointer"
              >
                查看全部 <i className="ri-arrow-right-line align-middle"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {featuredArticles.map((article) => (
                <ArticleCard key={article.id} article={article} variant="featured" />
              ))}
            </div>
          </div>
        </section>

        {/* Latest Deals */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950">
                最新优惠
              </h2>
              <Link
                to="/category/baicai-jia"
                className="text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap cursor-pointer"
              >
                更多优惠 <i className="ri-arrow-right-line align-middle"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {latestArticles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          </div>
        </section>

        {/* Hot Right Now */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-8">
              热门推荐
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {hotArticles.map((article) => (
                <ArticleCard key={article.id} article={article} variant="compact" />
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950 text-center mb-8">
              常见问题
            </h2>
            <div className="space-y-3">
              {faqData.map((item, idx) => (
                <div key={idx} className="rounded-xl border border-background-200/70 bg-background-50 overflow-hidden">
                  <button
                    onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                    className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                  >
                    <h4 className="font-heading text-base text-foreground-900 pr-4">{item.q}</h4>
                    <span className={`flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 transition-transform ${expandedFaq === idx ? 'rotate-180' : ''}`}>
                      <i className="ri-arrow-down-s-line text-sm"></i>
                    </span>
                  </button>
                  {expandedFaq === idx && (
                    <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                      {item.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Newsletter */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-2xl mx-auto text-center">
            <span className="w-14 h-14 mx-auto flex items-center justify-center rounded-2xl bg-primary-100 text-primary-500 mb-5">
              <i className="ri-mail-send-line text-2xl"></i>
            </span>
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-3">
              订阅每日优惠推送
            </h2>
            <p className="text-sm text-foreground-600 mb-6 leading-relaxed">
              不错过任何一个白菜好物，每日最新优惠资讯直接发送到你的邮箱。
            </p>
            {subscribed ? (
              <div className="py-4 px-6 rounded-xl bg-accent-100 text-accent-700 text-sm font-medium">
                <i className="ri-check-line mr-2"></i>订阅成功！感谢关注，我们会将最新优惠发送到您的邮箱。
              </div>
            ) : (
              <form
                data-readdy-form
                id="newsletter-form"
                onSubmit={handleNewsletterSubmit}
                className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 max-w-md mx-auto"
              >
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="请输入您的邮箱地址"
                  className="flex-1 px-4 py-3 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                />
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="anti-spam-field"
                />
                <button
                  type="submit"
                  className="px-6 py-3 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  立即订阅
                </button>
              </form>
            )}
            {formError && (
              <p className="mt-3 text-sm text-red-500">{formError}</p>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}